// Docker/OCI image-index API client (roadmap item 137 Phase B).
//
// Consumes the read-only `GET /api/v1/repos/{id}/docker/images` endpoint
// added in Phase A (`api/internal/handlers/repo_docker_images.go`), which
// aggregates `docker_tags`/`docker_manifests` per `image_path` so the
// Repo Detail Contents tab can render a human-readable "image → tags"
// view instead of the raw `manifests/<image>/sha256:<digest>` mirror
// path.

import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import { parseErrorResponse } from './problem';

export interface DockerImageTag {
  tag: string;
  digest: string;
  media_type: string;
  size_bytes: number;
  updated_at: string;
  has_provenance: boolean;
}

export interface DockerImage {
  image_path: string;
  tags: DockerImageTag[];
}

export interface DockerImagesResponse {
  images: DockerImage[];
  total_images: number;
  total_tags: number;
  has_more: boolean;
}

// Item 302 Phase A/B: image-level summary row (no embedded tags) and its
// keyset-paginated envelope, backing the expand-to-load-tags Repo Detail
// view so the row count no longer depends on the tag total.
export interface DockerImageSummary {
  image_path: string;
  tag_count: number;
  newest_tag: string;
  last_push: string;
  total_size_bytes: number;
}

interface DockerImagesSummaryPage {
  items: DockerImageSummary[];
  next_cursor?: string;
  has_more: boolean;
}

interface DockerImageTagsPage {
  items: DockerImageTag[];
  next_cursor?: string;
  has_more: boolean;
}

// CAS reaper GC status (roadmap item 271 part 1) — read-only aggregates
// from `GET /api/admin/docker/gc`, backing the Admin overview's GC status
// card.
export interface DockerGCStatus {
  pending_candidates: number;
  total_referenced: number;
}

const fetcher = async (url: string) => {
  const r = await fetch(url, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!r.ok) throw await parseErrorResponse(r);
  return r.json();
};

export function useDockerImages(repoID: string | undefined) {
  return useSWR<DockerImagesResponse>(
    repoID ? `/api/v1/repos/${repoID}/docker/images` : null,
    fetcher,
  );
}

// useDockerImagesPaginated — Item 302 Phase A. Cursor-paginated image
// summaries (25/page, sorted by last push) so a repo with thousands of
// images renders one row per image instead of one row per tag. Mirrors
// the useTokensPaginated pattern in api/tokens.ts.
export function useDockerImagesPaginated(repoID: string | undefined, limit = 25) {
  const swr = useSWRInfinite<DockerImagesSummaryPage>(
    (_idx, prev) => {
      if (!repoID) return null;
      if (prev && !prev.has_more) return null;
      const cursor = prev?.next_cursor ?? '';
      const cursorPart = cursor ? `&cursor=${encodeURIComponent(cursor)}` : '';
      return `/api/v1/repos/${repoID}/docker/images?paginated=1&limit=${limit}${cursorPart}`;
    },
    fetcher,
  );
  const items = swr.data ? swr.data.flatMap((p) => p.items) : [];
  const last = swr.data?.[swr.data.length - 1];
  return {
    items,
    error: swr.error,
    isLoading: swr.isLoading,
    isValidating: swr.isValidating,
    hasMore: last?.has_more ?? false,
    loadMore: () => swr.setSize(swr.size + 1),
  };
}

// useDockerImageTagsPaginated — Item 302 Phase B. Fetches the tag page
// for exactly one image, only while `enabled` (the row is expanded), so
// the per-tag manifest parse never runs for collapsed rows.
export function useDockerImageTagsPaginated(
  repoID: string | undefined,
  imagePath: string,
  enabled: boolean,
  limit = 20,
) {
  const swr = useSWRInfinite<DockerImageTagsPage>(
    (_idx, prev) => {
      if (!repoID || !enabled) return null;
      if (prev && !prev.has_more) return null;
      const cursor = prev?.next_cursor ?? '';
      const cursorPart = cursor ? `&cursor=${encodeURIComponent(cursor)}` : '';
      return `/api/v1/repos/${repoID}/docker/images/tags?image=${encodeURIComponent(imagePath)}&limit=${limit}${cursorPart}`;
    },
    fetcher,
  );
  const items = swr.data ? swr.data.flatMap((p) => p.items) : [];
  const last = swr.data?.[swr.data.length - 1];
  return {
    items,
    error: swr.error,
    isLoading: swr.isLoading,
    isValidating: swr.isValidating,
    hasMore: last?.has_more ?? false,
    loadMore: () => swr.setSize(swr.size + 1),
  };
}

export function useDockerGCStatus() {
  return useSWR<DockerGCStatus>('/api/admin/docker/gc', fetcher, { refreshInterval: 30_000 });
}
